import { useAbility } from '@casl/react';
import { ArrowForward } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Tooltip,
  Typography,
  lighten,
  useTheme,
} from '@mui/material';
import { useEffect, useState, type MouseEvent } from 'react';
import EntityIcon from 'src/components/Icons/EntityIcon';
import { useAdminUrlNavigation } from 'src/hooks/useAdminUrlNavigation';
import useEffectiveApplicationGroupId from 'src/hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from 'src/hooks/useEffectiveApplicationId';
import { useNavigateWithState } from 'src/hooks/useNavigateWithState';
import pages from 'src/router/routes';
import WidgetCard from 'switch-fe-shared/components/Cards/WidgetCard';
import { capitalize } from 'switch-fe-shared/utils/formatting';

import { formatNumber } from '../../../../../../switch-shared/src/utils/formatters';
import { ACTIONS } from '../../../../auth/utils/defineAbilityFor';
import { AbilityContext } from '../../../../context/canContext';
import { useDashboard } from '../../../../context/dashboardContext';
import { useGetMetersSummaryQuery } from '../../../../services/switch-rest-api/meter.service';
import {
  isAllSelection,
  isUtilityType,
  isAllUtilitySelection,
} from '../../../../utils/selection';
import { useAllGroupsMetrics } from '../../hooks/useAllGroupsMetrics';
import { onlineStatusColor } from '../../utils/onlineStatus';

const MetersWidget = () => {
  // TODO: check for prepaid status in separate call

  /* Hooks & Use Effects */

  const theme = useTheme();

  const { navigateWithApplicationContext } = useNavigateWithState();
  const { getApplicationScopedPath } = useAdminUrlNavigation();

  const { utility, zone } = useDashboard();

  const appGroupId = useEffectiveApplicationGroupId();
  const appId = useEffectiveApplicationId();

  const ability = useAbility(AbilityContext);
  const isAllGroupsSelected = isAllSelection(appGroupId);
  const isAllAppsSelected = isAllSelection(appId);
  const isUtilitySelected = isUtilityType(utility);
  const canNavigateToMeters =
    ability.can(ACTIONS.READ, 'Meters') &&
    appId != null &&
    appGroupId != null &&
    !isAllSelection(appId);

  /* All Groups Metrics Hook */
  const {
    metrics: allGroupsMetrics,
    isLoading: isAllGroupsLoading,
    isFetching: isAllGroupsFetching,
  } = useAllGroupsMetrics();

  /* States */

  const [online, setOnline] = useState({
    total: 0,
    value: 0,
  });

  const [onlinePrepaid, setOnlinePrepaid] = useState({
    total: 0,
    value: 0,
  });

  /* API Calls */

  const {
    data: metersSummary,
    isLoading: isMetersLoading,
    isFetching: isMetersFetching,
  } = useGetMetersSummaryQuery(
    {
      ...(!isAllAppsSelected && {
        applicationId: appId,
      }),
      ...(!isAllGroupsSelected && {
        applicationGroupId: appGroupId,
      }),
      ...(!isAllGroupsSelected &&
        isUtilitySelected && { utilityType: utility }),
      zoneId: zone?._id,
      limit: 0,
    },
    {
      skip:
        !ability.can(ACTIONS.READ, 'Meters') ||
        appGroupId == null ||
        isAllGroupsSelected,
      pollingInterval: 30_000,
    }
  );

  /* Functions & Functional Components */

  const getColor = (onlineCount: number, totalCount: number) =>
    onlineStatusColor(theme, onlineCount, totalCount);

  const getTooltipDescription = () => {
    if (isAllGroupsSelected) {
      return 'Online status of all meters across all groups';
    }

    const prepaidText =
      (onlinePrepaid?.total ?? 0) > 0 ? 'and prepaid meters' : '';

    if (zone?._id) {
      return `Online status of the meters ${prepaidText} in zone ${zone.name}`;
    }

    return `Online status of the meters ${prepaidText} in the selected ${
      appId != null && !isAllSelection(appId) ? 'application' : 'group'
    }`;
  };

  const getMetersFiltersUrl = (
    isOnlineFilter: boolean,
    isPrepaidFilter?: boolean
  ) => {
    const filters = [
      ...(isUtilityType(utility)
        ? [{ id: 'utilityType', value: utility }]
        : []),
      { id: 'isOnline', value: isOnlineFilter },
      ...(typeof isPrepaidFilter === 'boolean'
        ? [{ id: 'isPrepaid', value: isPrepaidFilter }]
        : []),
    ];
    const params = new URLSearchParams();
    params.set(
      'filters',
      filters.map(({ id, value }) => `${id}:${value}`).join(',')
    );
    return `${getApplicationScopedPath(
      'meters',
      pages.meters.path,
      appGroupId,
      appId
    )}?${params.toString()}`;
  };

  const handleNavigateToMeters = (
    event: MouseEvent<HTMLElement>,
    isOnlineFilter: boolean,
    isPrepaidFilter?: boolean
  ) => {
    event.stopPropagation();
    if (!canNavigateToMeters) {
      return;
    }
    navigateWithApplicationContext(
      appGroupId,
      appId,
      getMetersFiltersUrl(isOnlineFilter, isPrepaidFilter)
    );
  };

  useEffect(() => {
    if (isAllGroupsSelected) {
      setOnline({ total: 0, value: 0 });
      setOnlinePrepaid({ total: 0, value: 0 });
      return;
    }

    if (metersSummary != null) {
      const prepaidOnline = metersSummary.totalPrepaidOnline ?? 0;
      const prepaidOffline = metersSummary.totalPrepaidOffline ?? 0;
      const normalOnline = Math.max(
        0,
        (metersSummary.totalOnline ?? 0) - prepaidOnline
      );
      const normalOffline = Math.max(
        0,
        (metersSummary.totalOffline ?? 0) - prepaidOffline
      );

      setOnline({
        total: normalOnline + normalOffline,
        value: normalOnline,
      });
      setOnlinePrepaid({
        total: prepaidOnline + prepaidOffline,
        value: prepaidOnline,
      });
    } else {
      setOnline({ total: 0, value: 0 });
      setOnlinePrepaid({ total: 0, value: 0 });
    }
  }, [metersSummary, isAllGroupsSelected]);

  return (
    <WidgetCard
      title={`${capitalize(isAllGroupsSelected ? '' : utility)} Meters`}
      subheader={
        zone?.name ? `Count in ${zone.name}` : 'Total count and online status'
      }
      tooltipDescription={getTooltipDescription()}
      avatar={
        <Box
          sx={{
            padding: `${theme.tokens.spacing.sm}px`,
            height: `${theme.tokens.spacing.sm * 5}px`,
            width: `${theme.tokens.spacing.sm * 5}px`,
            mx: `-${theme.tokens.spacing.sm}px`,
            borderRadius: theme.tokens.borderRadius.full,
            background: theme.tokens.colors.card,
            boxShadow: theme.tokens.shadows.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.primary.main,
          }}
        >
          <EntityIcon type='meters' />
        </Box>
      }
      onClickFn={
        canNavigateToMeters
          ? () =>
              navigateWithApplicationContext(
                appGroupId,
                appId,
                getApplicationScopedPath(
                  'meters',
                  pages.meters.path,
                  appGroupId,
                  appId
                )
              )
          : undefined
      }
      disabled={!ability.can(ACTIONS.READ, 'Meters')}
      cardContentProps={{
        sx: {
          pt: `0 !important`,
          pb:
            (onlinePrepaid?.total ?? 0) > 0
              ? `2px !important`
              : `16px !important`,
          color: lighten(theme.palette.primary.main, 0.1),
        },
      }}
    >
      {isAllGroupsSelected ? (
        <>
          {isAllGroupsLoading || isAllGroupsFetching ? (
            <Grid
              height={'100%'}
              container
              alignItems={'center'}
              justifyContent={'center'}
            >
              <Grid item>
                <CircularProgress />
              </Grid>
            </Grid>
          ) : (
            <Grid
              container
              alignContent={'center'}
              justifyContent={'center'}
              height={'100%'}
            >
              <Grid item xs={12}>
                <Tooltip
                  slotProps={{
                    tooltip: {
                      sx: {
                        background: 'white',
                        color: theme.tokens.colors.cardForeground,
                        boxShadow: theme.tokens.shadows.lg,
                        borderRadius: theme.tokens.borderRadius.md,
                        padding: 0,
                        minWidth: 175,
                      },
                    },
                  }}
                  title={
                    <Box>
                      <Stack
                        spacing={1}
                        sx={{
                          px: `${theme.tokens.spacing.md}px`,
                          py: `${theme.tokens.spacing.sm}px`,
                        }}
                      >
                        <Stack
                          direction='row'
                          alignItems='center'
                          justifyContent='space-between'
                          spacing={2}
                        >
                          <Stack
                            direction='row'
                            alignItems='center'
                            spacing={1}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: theme.tokens.borderRadius.full,
                                backgroundColor: theme.palette.success.main,
                              }}
                            />
                            <Typography
                              variant='body2'
                              sx={{ color: theme.palette.text.secondary }}
                            >
                              Online
                            </Typography>
                          </Stack>
                          <Stack
                            direction='row'
                            alignItems='center'
                            spacing={1}
                          >
                            <Typography
                              variant='body2'
                              sx={{
                                fontWeight:
                                  theme.tokens.typography.fontWeight.semibold,
                              }}
                            >
                              {formatNumber(
                                allGroupsMetrics?.meters.online ?? 0,
                                0
                              )}
                            </Typography>
                            {canNavigateToMeters && (
                              <Button
                                size='small'
                                variant='text'
                                onClick={(event) =>
                                  handleNavigateToMeters(event, true)
                                }
                                sx={{ minWidth: 'auto' }}
                                aria-label='View'
                              >
                                <ArrowForward fontSize='small' />
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                        <Stack
                          direction='row'
                          alignItems='center'
                          justifyContent='space-between'
                          spacing={2}
                        >
                          <Stack
                            direction='row'
                            alignItems='center'
                            spacing={1}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: theme.tokens.borderRadius.full,
                                backgroundColor: theme.palette.error.main,
                              }}
                            />
                            <Typography
                              variant='body2'
                              sx={{ color: theme.palette.text.secondary }}
                            >
                              Offline
                            </Typography>
                          </Stack>
                          <Stack
                            direction='row'
                            alignItems='center'
                            spacing={1}
                          >
                            <Typography
                              variant='body2'
                              sx={{
                                fontWeight:
                                  theme.tokens.typography.fontWeight.semibold,
                              }}
                            >
                              {formatNumber(
                                allGroupsMetrics?.meters.offline ?? 0,
                                0
                              )}
                            </Typography>
                            {canNavigateToMeters && (
                              <Button
                                size='small'
                                variant='text'
                                onClick={(event) =>
                                  handleNavigateToMeters(event, false)
                                }
                                sx={{ minWidth: 'auto' }}
                                aria-label='View'
                              >
                                <ArrowForward fontSize='small' />
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                      </Stack>
                    </Box>
                  }
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        variant='body2'
                        sx={{
                          color: theme.palette.primary.main,
                          fontWeight: 700,
                          fontSize: '25px',
                          display: 'inline-block',
                          mr: `${theme.tokens.spacing.sm}px`,
                        }}
                      >
                        {formatNumber(allGroupsMetrics?.meters.total ?? 0, 0)}
                      </Typography>
                      <Chip
                        label={`${(
                          allGroupsMetrics?.meters.onlinePercentage ?? 0
                        ).toFixed(0)}%`}
                        sx={{
                          backgroundColor: getColor(
                            allGroupsMetrics?.meters.online ?? 0,
                            allGroupsMetrics?.meters.total ?? 0
                          ),
                          color: theme.palette.common.white,
                          fontWeight: 600,
                          fontSize: theme.tokens.typography.fontSize.lg,
                          height: 28,
                          cursor: 'default',
                        }}
                      />
                    </Box>
                  </Box>
                </Tooltip>
              </Grid>
            </Grid>
          )}
        </>
      ) : isMetersLoading || isMetersFetching ? (
        <Grid
          height={'100%'}
          container
          alignItems={'center'}
          justifyContent={'center'}
        >
          <Grid item>
            <CircularProgress />
          </Grid>
        </Grid>
      ) : (
        <>
          {metersSummary != null && (
            <Grid
              container
              alignContent={'center'}
              justifyContent={'center'}
              height={'100%'}
              gap={0.5}
            >
              <Grid item xs={12}>
                {online.total === 0 ? (
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant='body2'>
                      {`No ${isUtilityType(utility) ? utility : ''} meters found`}
                    </Typography>
                  </Box>
                ) : (
                  <Tooltip
                    slotProps={{
                      tooltip: {
                        sx: {
                          background: 'white',
                          color: theme.tokens.colors.cardForeground,
                          boxShadow: theme.tokens.shadows.lg,
                          borderRadius: theme.tokens.borderRadius.md,
                          padding: 0,
                          minWidth: 175,
                        },
                      },
                    }}
                    title={
                      <Box>
                        <Stack
                          spacing={1}
                          sx={{
                            px: `${theme.tokens.spacing.md}px`,
                            py: `${theme.tokens.spacing.sm}px`,
                          }}
                        >
                          {(onlinePrepaid?.total ?? 0) > 0 && (
                            <Typography
                              variant='body2'
                              sx={{
                                color: theme.tokens.colors.cardForeground,
                                textAlign: 'left',
                                fontWeight: 'bold',
                                pt: 0.5,
                              }}
                            >
                              Normal Meters
                            </Typography>
                          )}
                          <Stack
                            direction='row'
                            alignItems='center'
                            justifyContent='space-between'
                            spacing={2}
                          >
                            <Stack
                              direction='row'
                              alignItems='center'
                              spacing={1}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: theme.tokens.borderRadius.full,
                                  backgroundColor: theme.palette.success.main,
                                }}
                              />
                              <Typography
                                variant='body2'
                                sx={{ color: theme.palette.text.secondary }}
                              >
                                Online
                              </Typography>
                            </Stack>
                            <Stack
                              direction='row'
                              alignItems='center'
                              spacing={1}
                            >
                              <Typography
                                variant='body2'
                                sx={{
                                  fontWeight:
                                    theme.tokens.typography.fontWeight.semibold,
                                }}
                              >
                                {formatNumber(online.value, 0)}
                              </Typography>
                              {canNavigateToMeters && (
                                <Button
                                  size='small'
                                  variant='text'
                                  onClick={(event) =>
                                    handleNavigateToMeters(event, true)
                                  }
                                  disabled={!canNavigateToMeters}
                                  sx={{
                                    minWidth: 'auto',
                                    py: `1px`,
                                    px: 1,
                                  }}
                                  aria-label='View'
                                >
                                  <ArrowForward fontSize='small' />
                                </Button>
                              )}
                            </Stack>
                          </Stack>
                          <Stack
                            direction='row'
                            alignItems='center'
                            justifyContent='space-between'
                            spacing={2}
                          >
                            <Stack
                              direction='row'
                              alignItems='center'
                              spacing={1}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: theme.tokens.borderRadius.full,
                                  backgroundColor: theme.palette.error.main,
                                }}
                              />
                              <Typography
                                variant='body2'
                                sx={{ color: theme.palette.text.secondary }}
                              >
                                Offline
                              </Typography>
                            </Stack>
                            <Stack
                              direction='row'
                              alignItems='center'
                              spacing={1}
                            >
                              <Typography
                                variant='body2'
                                sx={{
                                  fontWeight:
                                    theme.tokens.typography.fontWeight.semibold,
                                }}
                              >
                                {formatNumber(online.total - online.value, 0)}
                              </Typography>
                              {canNavigateToMeters && (
                                <Button
                                  size='small'
                                  variant='text'
                                  onClick={(event) =>
                                    handleNavigateToMeters(event, false)
                                  }
                                  sx={{
                                    minWidth: 'auto',
                                    py: `1px`,
                                    px: 1,
                                  }}
                                  aria-label='View'
                                >
                                  <ArrowForward fontSize='small' />
                                </Button>
                              )}
                            </Stack>
                          </Stack>
                        </Stack>
                      </Box>
                    }
                  >
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          variant='body2'
                          sx={{
                            color: theme.palette.primary.main,
                            fontWeight: 700,
                            fontSize: '25px',
                            display: 'inline-block',
                            mr: `${theme.tokens.spacing.sm}px`,
                          }}
                        >
                          {formatNumber(online.total, 0)}
                        </Typography>
                        <Chip
                          label={`${(
                            (online.value / online.total) *
                            100
                          ).toFixed(0)}%`}
                          sx={{
                            backgroundColor: getColor(
                              online.value,
                              online.total
                            ),
                            color: theme.palette.common.white,
                            fontWeight: 600,
                            fontSize: theme.tokens.typography.fontSize.lg,
                            height: 28,
                            cursor: 'default',
                          }}
                        />
                      </Box>
                    </Box>
                  </Tooltip>
                )}
              </Grid>
              {(onlinePrepaid?.total ?? 0) > 0 && (
                <Grid item xs={12} container justifyContent={'center'}>
                  <Grid item>
                    <Tooltip
                      slotProps={{
                        tooltip: {
                          sx: {
                            background: 'white',
                            color: theme.tokens.colors.cardForeground,
                            boxShadow: theme.tokens.shadows.lg,
                            borderRadius: theme.tokens.borderRadius.md,
                            padding: 0,
                            minWidth: 175,
                          },
                        },
                      }}
                      title={
                        <Box>
                          <Stack
                            spacing={1}
                            sx={{
                              px: `${theme.tokens.spacing.md}px`,
                              py: `${theme.tokens.spacing.sm}px`,
                            }}
                          >
                            <Typography
                              variant='body2'
                              sx={{
                                color: theme.tokens.colors.cardForeground,
                                textAlign: 'left',
                                fontWeight: 'bold',
                                pt: 0.5,
                              }}
                            >
                              Prepaid Meters
                            </Typography>
                            <Stack
                              direction='row'
                              alignItems='center'
                              justifyContent='space-between'
                              spacing={2}
                            >
                              <Stack
                                direction='row'
                                alignItems='center'
                                spacing={1}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius:
                                      theme.tokens.borderRadius.full,
                                    backgroundColor: theme.palette.success.main,
                                  }}
                                />
                                <Typography
                                  variant='body2'
                                  sx={{ color: theme.palette.text.secondary }}
                                >
                                  Online
                                </Typography>
                              </Stack>
                              <Stack
                                direction='row'
                                alignItems='center'
                                spacing={1}
                              >
                                <Typography
                                  variant='body2'
                                  sx={{
                                    fontWeight:
                                      theme.tokens.typography.fontWeight
                                        .semibold,
                                  }}
                                >
                                  {formatNumber(onlinePrepaid.value, 0)}
                                </Typography>
                                {canNavigateToMeters && (
                                  <Button
                                    size='small'
                                    variant='text'
                                    onClick={(event) =>
                                      handleNavigateToMeters(event, true, true)
                                    }
                                    sx={{
                                      minWidth: 'auto',
                                      py: `1px`,
                                      px: 1,
                                    }}
                                    aria-label='View'
                                  >
                                    <ArrowForward fontSize='small' />
                                  </Button>
                                )}
                              </Stack>
                            </Stack>
                            <Stack
                              direction='row'
                              alignItems='center'
                              justifyContent='space-between'
                              spacing={2}
                            >
                              <Stack
                                direction='row'
                                alignItems='center'
                                spacing={1}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius:
                                      theme.tokens.borderRadius.full,
                                    backgroundColor: theme.palette.error.main,
                                  }}
                                />
                                <Typography
                                  variant='body2'
                                  sx={{ color: theme.palette.text.secondary }}
                                >
                                  Offline
                                </Typography>
                              </Stack>
                              <Stack
                                direction='row'
                                alignItems='center'
                                spacing={1}
                              >
                                <Typography
                                  variant='body2'
                                  sx={{
                                    fontWeight:
                                      theme.tokens.typography.fontWeight
                                        .semibold,
                                  }}
                                >
                                  {formatNumber(
                                    onlinePrepaid.total - onlinePrepaid.value,
                                    0
                                  )}
                                </Typography>
                                {canNavigateToMeters && (
                                  <Button
                                    size='small'
                                    variant='text'
                                    onClick={(event) =>
                                      handleNavigateToMeters(event, false, true)
                                    }
                                    sx={{
                                      minWidth: 'auto',
                                      py: `1px`,
                                      px: 1,
                                    }}
                                    aria-label='View'
                                  >
                                    <ArrowForward fontSize='small' />
                                  </Button>
                                )}
                              </Stack>
                            </Stack>
                          </Stack>
                        </Box>
                      }
                    >
                      <Box
                        sx={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            variant='body2'
                            sx={{
                              color: theme.palette.primary.main,
                              fontWeight: 700,
                              fontSize: '25px',
                              display: 'inline-block',
                              mr: `${theme.tokens.spacing.sm}px`,
                            }}
                          >
                            {formatNumber(onlinePrepaid.total, 0)}
                          </Typography>
                          <Chip
                            label={`${(
                              (onlinePrepaid.value / onlinePrepaid.total) *
                              100
                            ).toFixed(0)}%`}
                            sx={{
                              backgroundColor: getColor(
                                onlinePrepaid.value,
                                onlinePrepaid.total
                              ),
                              color: theme.palette.common.white,
                              fontWeight: 600,
                              fontSize: theme.tokens.typography.fontSize.lg,
                              height: 28,
                              cursor: 'default',
                            }}
                          />
                        </Box>
                      </Box>
                    </Tooltip>
                  </Grid>
                </Grid>
              )}
            </Grid>
          )}
        </>
      )}
    </WidgetCard>
  );
};

export default MetersWidget;
